import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  buildFinalizePayloadItem,
  formatCurrency,
  formatPercent,
  pickRecommendationForPrefill,
  titleCase,
  toFiniteNumber,
} from "../report.utils";

const ACTION_TYPE_OPTIONS = [
  { value: "promotion", label: "Promotion" },
  { value: "increment", label: "Increment" },
  { value: "bonus", label: "Bonus" },
];

const STEP_LABELS = ["Action Setup", "Per Employee", "Confirm"];

const buildInitialForm = (items = []) => {
  let defaultActionType = "";

  const itemConfig = {};
  items.forEach((item) => {
    const recommendation = pickRecommendationForPrefill(item);
    if (!defaultActionType && recommendation?.action_type) {
      defaultActionType = recommendation.action_type;
    }

    itemConfig[item.id] = {
      amount: recommendation?.amount ?? "",
      new_position_id: recommendation?.new_position_id ?? "",
      recommendation,
    };
  });

  return {
    action_type: defaultActionType,
    effective_date: new Date().toISOString().slice(0, 10),
    reason: "",
    itemConfig,
  };
};

const validateGlobal = (form) => {
  const errors = {};

  if (!form?.action_type) errors.action_type = "Action type is required.";
  if (!form?.effective_date) errors.effective_date = "Effective date is required.";

  const reason = String(form?.reason || "").trim();
  if (!reason) errors.reason = "Reason is required.";
  else if (reason.length < 10) errors.reason = "Reason must be at least 10 characters.";

  return errors;
};

const validateItems = (form, items) => {
  const errors = {};

  items.forEach((item) => {
    const cfg = form?.itemConfig?.[item.id] || {};

    if (form.action_type === "promotion") {
      if (!cfg?.new_position_id) {
        errors[item.id] = "New position is required for promotion.";
      }
      return;
    }

    if (["increment", "bonus"].includes(form.action_type)) {
      const amount = toFiniteNumber(cfg?.amount);
      if (amount === null || amount <= 0) {
        errors[item.id] = "Amount must be greater than 0.";
      }
    }
  });

  return errors;
};

export default function FinalizeActionDialog({
  open,
  items,
  positions,
  submitting,
  submitError,
  onClose,
  onSubmit,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(buildInitialForm(items));
  const [globalErrors, setGlobalErrors] = useState({});
  const [itemErrors, setItemErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setForm(buildInitialForm(items));
    setGlobalErrors({});
    setItemErrors({});
  }, [open, items]);

  const totalPayout = useMemo(() => {
    if (!["increment", "bonus"].includes(form.action_type)) return 0;

    return items.reduce((acc, item) => {
      const cfg = form?.itemConfig?.[item.id] || {};
      const amount = toFiniteNumber(cfg?.amount) || 0;
      return acc + amount;
    }, 0);
  }, [items, form]);

  const preparedPayload = useMemo(() => {
    const payloadItems = items.map((item) => {
      const cfg = form?.itemConfig?.[item.id] || {};
      return buildFinalizePayloadItem(item, form.action_type, cfg);
    });

    return {
      action_type: form.action_type,
      effective_date: form.effective_date,
      reason: String(form.reason || "").trim(),
      items: payloadItems,
    };
  }, [items, form]);

  const handleUpdateConfig = (id, key, value) => {
    setForm((prev) => ({
      ...prev,
      itemConfig: {
        ...prev.itemConfig,
        [id]: {
          ...(prev.itemConfig?.[id] || {}),
          [key]: value,
        },
      },
    }));
  };

  const goNext = () => {
    if (activeStep === 0) {
      const errs = validateGlobal(form);
      setGlobalErrors(errs);
      if (Object.keys(errs).length) return;
    }

    if (activeStep === 1) {
      const errs = validateItems(form, items);
      setItemErrors(errs);
      if (Object.keys(errs).length) return;
    }

    setActiveStep((s) => Math.min(s + 1, STEP_LABELS.length - 1));
  };

  const goBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    const errsGlobal = validateGlobal(form);
    const errsItems = validateItems(form, items);
    setGlobalErrors(errsGlobal);
    setItemErrors(errsItems);
    if (Object.keys(errsGlobal).length || Object.keys(errsItems).length) {
      if (Object.keys(errsGlobal).length) setActiveStep(0);
      else setActiveStep(1);
      return;
    }

    await onSubmit(preparedPayload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: "8px" } }}
    >
      <DialogTitle>Finalize Action</DialogTitle>
      <DialogContent
        dividers
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
          },
        }}
      >
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEP_LABELS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {submitError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        ) : null}

        {activeStep === 0 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label="Action type"
                value={form.action_type}
                onChange={(e) => setForm((p) => ({ ...p, action_type: e.target.value }))}
                fullWidth
                error={Boolean(globalErrors.action_type)}
                helperText={globalErrors.action_type}
              >
                {ACTION_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                type="date"
                label="Effective date"
                value={form.effective_date}
                onChange={(e) => setForm((p) => ({ ...p, effective_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
                error={Boolean(globalErrors.effective_date)}
                helperText={globalErrors.effective_date}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Reason"
                value={form.reason}
                onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                fullWidth
                multiline
                minRows={3}
                error={Boolean(globalErrors.reason)}
                helperText={globalErrors.reason || "Minimum 10 characters"}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                Default values are prefilled from approved HR recommendation when available; otherwise from latest
                proposed recommendation.
              </Alert>
            </Grid>
          </Grid>
        ) : null}

        {activeStep === 1 ? (
          <Stack spacing={1.5}>
            {items.map((item) => {
              const cfg = form?.itemConfig?.[item.id] || {};
              const recommendation = cfg?.recommendation;

              return (
                <Paper key={item.id} variant="outlined" sx={{ p: 1.5, borderRadius: "8px" }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Typography sx={{ fontWeight: 700 }}>{item?.employee?.name || "-"}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item?.employee?.employee_code || "-"} | Final: {formatPercent(item?.final_percentage, 1)}
                      </Typography>
                    </Grid>

                    {form.action_type === "promotion" ? (
                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          label="New position"
                          value={cfg?.new_position_id || ""}
                          onChange={(e) => handleUpdateConfig(item.id, "new_position_id", e.target.value)}
                          fullWidth
                          error={Boolean(itemErrors[item.id])}
                          helperText={itemErrors[item.id] || ""}
                        >
                          <MenuItem value="">Select position</MenuItem>
                          {(positions || []).map((pos) => (
                            <MenuItem key={String(pos?.id ?? pos?.name)} value={String(pos?.id ?? pos?.name)}>
                              {pos?.name || pos?.label || pos?.id}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    ) : (
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="Amount"
                          type="number"
                          value={cfg?.amount ?? ""}
                          onChange={(e) => handleUpdateConfig(item.id, "amount", e.target.value)}
                          fullWidth
                          inputProps={{ min: 0 }}
                          error={Boolean(itemErrors[item.id])}
                          helperText={itemErrors[item.id] || ""}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={4}>
                      {recommendation ? (
                        <Alert severity="info" sx={{ py: 0 }}>
                          Prefill: {titleCase(recommendation.action_type)} ({titleCase(recommendation.status)})
                        </Alert>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No recommendation found.
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        ) : null}

        {activeStep === 2 ? (
          <Stack spacing={1.5}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: "8px" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Preview
              </Typography>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ my: 1 }}>
                <Chip label={`Action: ${titleCase(form.action_type)}`} sx={{ borderRadius: "8px" }} />
                <Chip label={`Effective: ${form.effective_date || "-"}`} sx={{ borderRadius: "8px" }} />
                <Chip label={`Employees: ${items.length}`} sx={{ borderRadius: "8px" }} />
                {form.action_type !== "promotion" ? (
                  <Chip
                    label={`Total payout: ${formatCurrency(totalPayout)}`}
                    color="warning"
                    variant="outlined"
                    sx={{ borderRadius: "8px" }}
                  />
                ) : null}
              </Stack>

              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                Reason: {form.reason || "-"}
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ borderRadius: "8px", overflow: "hidden" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Final %</TableCell>
                    <TableCell>{form.action_type === "promotion" ? "New Position" : "Amount"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => {
                    const cfg = form?.itemConfig?.[item.id] || {};
                    return (
                      <TableRow key={item.id}>
                        <TableCell>{item?.employee?.name || "-"}</TableCell>
                        <TableCell>{item?.employee?.employee_code || "-"}</TableCell>
                        <TableCell>{formatPercent(item?.final_percentage, 1)}</TableCell>
                        <TableCell>
                          {form.action_type === "promotion"
                            ? cfg?.new_position_id || "-"
                            : formatCurrency(cfg?.amount)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting} sx={{ borderRadius: "8px" }}>
          Cancel
        </Button>
        <Button onClick={goBack} disabled={activeStep === 0 || submitting} sx={{ borderRadius: "8px" }}>
          Back
        </Button>

        {activeStep < STEP_LABELS.length - 1 ? (
          <Button variant="contained" onClick={goNext} disabled={submitting} sx={{ borderRadius: "8px" }}>
            Next
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={{ borderRadius: "8px" }}>
            {submitting ? "Submitting..." : "Confirm & Submit"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
